/**
 * 海报卡墙的**目的分组**(V1 §3.3,2026-07-25)—— 卡按「用户想干什么」分组。
 * 现状(2026-08-05 最终版):上墙 10 张分 2 组(上架卖货 4 + 对外宣传 4,另加页首自由创作 2 张),
 * 其余 6 张见 {@link POSTER_HIDDEN_CARD_KEYS}。
 *
 * ═══ 为什么要分组,以及为什么分组不写进卡字面量 ═══════════════════════════════════
 * 改之前 `PosterHubSections` 三节是 **三个不同维度**:商品出图 3 张按*产物形制*
 * (1 张 / N 张 / 3 比例)、长图工坊 10 张按*目的*(电商详情 / 企业介绍 / …)、
 * 横宽 1 张又回到形制。新用户得先学会「主图 / 组图 / 套图 / 长图 / 宽图」这套产品术语
 * 才找得到自己要的东西 —— 而首页发现流刚把 17 舱收成 5 群横滑带,舱内页退回平铺长墙,
 * 语言不接。这里把维度统一成**目的**:形制降级为卡面的几何图示(它仍然有用,
 * 只是不再当分组依据)。
 *
 * ★分组表**独立成文件、不给 `SceneCard` 加字段**,是刻意的工程取舍:
 *   加 `purposeGroup` 字段要动 `model/types.ts` + 14 张卡的字面量,而视频线同期正在改
 *   `types.ts` / `SceneCapCard.tsx` —— 两条线会在同一批行上正面撞。放这里之后,
 *   `catalog.ts` 那侧只剩海报卡 `costLabel` 几行改动,与 `VIDEO_CARDS` 区段不重叠。
 *   代价是「新增一张卡忘了归组」编译器管不了 → 由 `posterPurpose.test.ts` 全覆盖锁死。
 *
 * ★组副标说的是**该组的判断依据**(什么时候选这组),不说实现。
 *   现状「点卡带预设进创建页 · 样图为真产物」是实现语言,用户读完仍不知道该不该点。
 */
import {
  POSTER_GOODS_CARDS,
  POSTER_LONGIMAGE_CARDS,
  POSTER_TRYON_CARD,
  POSTER_WIDE_CARD,
} from "../catalog";
import type { SceneCard } from "./types";

export interface PosterPurposeGroup {
  /** ascii 侧写:`data-purpose-group` / i18n key / 测试定位同源。 */
  key: string;
  /** 组标题(卡墙上的那一行)。 */
  title: string;
  /** 组副标 = 判断依据。★禁实现语言(测试锁)。 */
  sub: string;
  /** 组内卡 key —— 顺序即卡墙渲染顺序。 */
  cardKeys: readonly string[];
}

/**
 * ★`as const satisfies` 缺一不可:
 *   只写 `satisfies` → `cardKeys` 塌成 `string[]`,拼错的 key 编译期不报;
 *   只写 `as const`  → 结构写歪了(少个 sub)也不报。
 * 两个一起写才是「字面量收窄 + 结构受检」。
 */
export const POSTER_PURPOSE_GROUPS = [
  {
    key: "sell",
    title: "上架卖货",
    sub: "把商品挂出去、让人下单的图",
    cardKeys: ["poster_single", "poster_group", "poster_multiratio", "longimage_goods"],
  },
  /**
   * 2026-08-05 最终版:14 张收到 8 张,原「种草发帖 / 门店与活动 / 企业形象 / 经营与投放」
   * 四组里留下的企业介绍 / 活动海报 / 榜单 / 横宽合成这一组,其余进 POSTER_HIDDEN_CARD_KEYS。
   *
   * ★为什么四张合一组、而不是保留原来那四组:每组只剩 1~2 张卡,一个组标题下挂一张卡,
   *   墙面读起来是空的。分组是为了让用户少读几张卡就找到自己要的,组比卡还多就起了反作用。
   * ★这一组里既有长图也有单帧横宽 —— 分组依据仍是**目的**(发给谁、发到哪儿),
   *   形制留在卡面图示上,别拿它当分组线(那正是 07-25 分组改版要治的病)。
   */
  {
    key: "outreach",
    title: "对外宣传",
    sub: "发给客户、发到场子里的图",
    // 2026-08-12:换衣**接替横宽腾出来的那个位子**(老板口径:换衣是新功能顶掉横宽,
    // 卡墙保持 2/4/4 三行不变)。横宽收进 POSTER_HIDDEN_CARD_KEYS 后本组只剩 3 张,
    // 换衣补上刚好回到 4 —— 而「上架卖货」也回到 4,墙面节奏不动。
    // ★留个明账:按**目的**分组的话,换衣(拍能上架的商品图)更贴「上架卖货」。
    //   这里是**布局优先**的决定,不是语义判断。要挪回去只是把 key 在两个组之间搬一下,
    //   但那样卖货组会变 5 张、宣传组 3 张,回到 2/5/3 的参差 —— 挪之前先想清楚这个代价。
    cardKeys: ["longimage_corp", "longimage_event", "longimage_rank", "tryon"],
  },
] as const satisfies readonly PosterPurposeGroup[];

/**
 * 暂时隐藏的卡 —— **是隐藏不是删**。
 *
 * 这张表反复开合过三轮,写全是为了下次别再当成「谁手滑删的」:
 *   2026-07-31 首屏收到 6 张(按「做好一张放一张」)→ 2026-08-03 用户拍板**全部放开**
 *   (corp 线做好 + 入口词面用途化,14 张分五组回墙)→ 2026-08-05 定**最终版**:
 *   企业介绍 / 活动海报 / 榜单 / 横宽留在墙上,其余六张收起。
 * ★**功能一行没动**:这些卡背后是同一个长图搭建器的 10 种用途,进任意一张长图卡
 *   之后用途下拉仍然切得到全部 10 种;老链接、历史作品照常打得开。收的只是首屏摆几张卡。
 * ★`posterPurpose.test.ts` 的全覆盖锁按「上墙 + 隐藏 = catalog 全集」校验,
 *   所以漏掉一张 key 仍然会红 —— 隐藏不等于失去看管。要放回来:把 key 挪回上面的组。
 */
export const POSTER_HIDDEN_CARD_KEYS = [
  "longimage_review", // 产品测评长图(08-03 版在「上架卖货」组)
  "longimage_social", // 社交种草长图(08-03 版在「种草发帖」组)
  "longimage_tutorial", // 教程攻略长图(同上)
  "longimage_menu", // 菜单价目单(08-03 版在「门店与活动」组)
  "longimage_hire", // 招聘长图(08-03 版在「企业形象」组)
  "longimage_report", // 数据战报长图(08-03 版在「经营与投放」组)
  "poster_wide", // 横宽宽图(08-12 用户拍板暂时收起;搭建器横宽能力、深链、历史作品全保留)
] as const;

/**
 * 卡墙「最常用」推荐描边 —— **全舱只允许一张**(§3.3)。
 * 两张以上就等于没推荐;这条由测试锁,不靠自觉。
 */
export const POSTER_RECOMMENDED_CARD_KEY = "longimage_goods";

/**
 * 卡墙覆盖的全部海报卡(查表用;顺序无意义,取组内顺序请走 posterCardsOfGroup)。
 *
 * ★**新增一张海报卡必须挂进这张表**:`posterCardsOfGroup` 查不到的 key 会被静默过滤掉
 *   (那是为了「配错一个 key 不至于把整面墙炸掉」),于是组里写了、墙上却不出现,
 *   而且一条错误都没有。`posterPurpose.test.ts` 的全覆盖锁按同一张表校验。
 */
const POSTER_CARD_INDEX: ReadonlyMap<string, SceneCard> = new Map(
  [...POSTER_GOODS_CARDS, ...POSTER_LONGIMAGE_CARDS, POSTER_WIDE_CARD, POSTER_TRYON_CARD].map(
    (c) => [c.key, c]
  )
);

/** 卡 key → 目的组;未归组 / 非海报卡一律 `null`(不猜、不兜底进某个组)。 */
export function purposeGroupOf(cardKey: string): PosterPurposeGroup | null {
  // `as const` 把 cardKeys 收窄成字面量元组,`includes` 的形参会被推成各组字面量的**交集**
  // (= never)。这里放宽回 readonly string[] 只是为了查表;收窄的价值在写入侧(拼错就编译红)。
  return (
    POSTER_PURPOSE_GROUPS.find((g) => (g.cardKeys as readonly string[]).includes(cardKey)) ?? null
  );
}

/**
 * 组 key → 该组的卡对象(按 `cardKeys` 顺序)。
 * 表里写了但 catalog 里没有的 key 会被过滤掉 —— 这种情况测试已经拦下,
 * 运行时静默跳过是为了「配错一个 key 不至于把整面墙炸掉」。
 */
export function posterCardsOfGroup(groupKey: string): SceneCard[] {
  const group = POSTER_PURPOSE_GROUPS.find((g) => g.key === groupKey);
  if (!group) return [];
  return group.cardKeys
    .map((k) => POSTER_CARD_INDEX.get(k))
    .filter((c): c is SceneCard => c !== undefined);
}
